import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { usersApi } from '../../api/users.api'
import { rolesApi } from '../../api/roles.api'
import { ApiError } from '../../api/client'
import { toast } from '../../store/toastStore'
import type { User } from '../../api/types'
import formStyles from '../../components/ui/Form.module.css'

interface UserFormModalProps {
  user?: User | null
  onClose: () => void
}

export function UserFormModal({ user, onClose }: UserFormModalProps) {
  const isEdit = Boolean(user)
  const queryClient = useQueryClient()

  const [username, setUsername] = useState(user?.username ?? '')
  const [displayName, setDisplayName] = useState(user?.displayName ?? '')
  const [email, setEmail] = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [roleIds, setRoleIds] = useState<string[]>(user?.roles.map((r) => r.role.id) ?? [])
  const [error, setError] = useState<string | null>(null)

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: async () => (await rolesApi.list()).data })

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit && user) {
        await usersApi.update(user.id, { displayName, email })
        await usersApi.assignRoles(user.id, roleIds)
      } else {
        await usersApi.create({ username, displayName, email, password, roleIds })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.success(isEdit ? 'Benutzer aktualisiert.' : 'Benutzer angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  const toggleRole = (roleId: string) => {
    setRoleIds((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]))
  }

  return (
    <Modal
      title={isEdit ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form className={formStyles.field} style={{ gap: 'var(--space-4)' }} onSubmit={onSubmit}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        {!isEdit ? (
          <Field label="Benutzername" htmlFor="username">
            <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
          </Field>
        ) : null}

        <Field label="Anzeigename" htmlFor="displayName">
          <Input id="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </Field>

        <Field label="E-Mail (optional)" htmlFor="email">
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>

        {!isEdit ? (
          <Field label="Initiales Passwort" htmlFor="password" hint="Mindestens 10 Zeichen">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={10}
            />
          </Field>
        ) : null}

        <Field label="Rollen">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {rolesQuery.data?.map((role) => (
              <label
                key={role.id}
                className={formStyles.checkboxRow}
                style={{
                  border: '1px solid var(--surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '6px 10px',
                }}
              >
                <input type="checkbox" checked={roleIds.includes(role.id)} onChange={() => toggleRole(role.id)} />
                {role.name}
              </label>
            ))}
          </div>
        </Field>
      </form>
    </Modal>
  )
}
